import {
  ParamsForMethod,
  useInboxConversation,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { withKey } from "@tellescope/ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInboxThreadDraft } from "../../hooks";
import { useInboxMessageDeleteMutations } from "../../../../hooks";
import { pendo } from "../../../../tracking";
import { MessageCompositionFooter } from "../../../../components/MessageCompositionFooter";
import { buildSmsMessageWithFiles } from "../../utils";

export interface InboxThreadSMSCompositionFooterProps {
  threadId: string;
  conversationData?: ReturnType<typeof useInboxConversation>;
}

export const InboxThreadSMSCompositionFooter = withKey(
  ({ threadId, conversationData }: InboxThreadSMSCompositionFooterProps) => {
    const { t } = useTranslation("inbox");
    const { deleteSmsMutation } = useInboxMessageDeleteMutations();
    const [composerKey, setComposerKey] = useState(0);

    const internalConversationData = useInboxConversation(threadId, { enabled: !conversationData });
    const resolvedConversationData = conversationData ?? internalConversationData;
    const { metadata, messages, drafts } = resolvedConversationData;
    const enduserId = metadata?.enduserIds.at(0);
    const enduserPhoneNumber = metadata?.enduserPhoneNumber;
    const phoneNumber = metadata?.phoneNumber;

    const { queryParams, setQueryParams, matchingDraft, isEditingScheduled, sendAtInPast, shouldQueryDraft } =
      useInboxThreadDraft({ conversationData: resolvedConversationData });

    const draftQuery = useTellescopeQuery({
      model: "sms_messages",
      method: "getOne",
      request: queryParams.draftId ?? "",
      enabled: shouldQueryDraft,
    });

    const latestDraft = useMemo(() => {
      if (queryParams.draftId) {
        return matchingDraft?.content ?? draftQuery.data?.message;
      }
      return drafts.at(0)?.content;
    }, [draftQuery.data, drafts, matchingDraft, queryParams.draftId]);

    const metadataForSendRequest = useMemo<
      Omit<ParamsForMethod<"sms_messages", "createOne">, "message"> | undefined
    >(() => {
      if (!enduserId) return undefined;
      return {
        enduserId,
        enduserPhoneNumber,
        phoneNumber,
        newThread: messages.length === 0,
        inbound: false,
      };
    }, [enduserId, enduserPhoneNumber, phoneNumber, messages.length]);

    const onMutationSuccess = () => {
      setComposerKey((k) => k + 1);
      setQueryParams((prev) => ({ ...prev, draftId: undefined }));
    };

    const sendMessageMutation = useTellescopeMutation({
      model: "sms_messages",
      method: "createOne",
      onSuccess: onMutationSuccess,
    });

    const updateMessageMutation = useTellescopeMutation({
      model: "sms_messages",
      method: "updateOne",
      onSuccess: onMutationSuccess,
    });

    const canSend = !!metadata && !!metadataForSendRequest && !sendMessageMutation.isPending;

    return (
      <MessageCompositionFooter
        key={composerKey}
        placeholder={t("Type your reply...")}
        autoFocus
        submitOnEnter={!isEditingScheduled}
        initialMessage={latestDraft}
        canSend={canSend}
        isUpdatePending={updateMessageMutation.isPending}
        enduserId={enduserId}
        channelType="sms"
        isEditingScheduled={isEditingScheduled}
        sendAtInPast={sendAtInPast}
        initDate={matchingDraft?.sendAt}
        scheduleDialogKey={matchingDraft?.sendAt?.toISOString() ?? "no-init-date"}
        onSubmit={(payload) => {
          if (!metadataForSendRequest) return;
          pendo.track("messaging-sms-thread-compose-send");
          const draftToDelete = matchingDraft;
          sendMessageMutation.mutate(
            { message: buildSmsMessageWithFiles(payload.message, payload.files), ...metadataForSendRequest },
            {
              onSuccess: () => {
                if (draftToDelete) deleteSmsMutation.mutate(draftToDelete.id);
              },
            }
          );
        }}
        onSchedule={(payload, sendAt) => {
          if (matchingDraft) {
            updateMessageMutation.mutate({
              id: matchingDraft.id,
              sendAt,
              message: buildSmsMessageWithFiles(payload.message, payload.files),
            });
          } else if (metadataForSendRequest) {
            sendMessageMutation.mutate({
              message: buildSmsMessageWithFiles(payload.message, payload.files),
              sendAt,
              ...metadataForSendRequest,
            });
          }
        }}
        onSendNow={(payload) => {
          if (!matchingDraft) return;
          updateMessageMutation.mutate({
            id: matchingDraft.id,
            message: buildSmsMessageWithFiles(payload.message, payload.files),
            sendAt: new Date(),
          });
        }}
        onSaveScheduled={(payload) => {
          if (!matchingDraft) return;
          updateMessageMutation.mutate({
            id: matchingDraft.id,
            message: buildSmsMessageWithFiles(payload.message, payload.files),
          });
        }}
        onReschedule={(payload, sendAt) => {
          if (!matchingDraft) return;
          updateMessageMutation.mutate({
            id: matchingDraft.id,
            sendAt,
            message: buildSmsMessageWithFiles(payload.message, payload.files),
          });
        }}
        trackingIds={{
          schedule: "sms-thread-schedule-message-dialog-open",
          reschedule: "sms-thread-reschedule-message-dialog-open",
        }}
      />
    );
  },
  "threadId"
);
